import React, { useEffect, useMemo, useRef, useState } from "react";
import styled, { css, keyframes } from "styled-components";
import {
  FaCalendarAlt,
  FaFileAlt,
  FaHeadset,
  FaPaperPlane,
  FaPills,
  FaPlus,
  FaRobot,
  FaUser,
  FaUserMd,
} from "react-icons/fa";
import { Star } from "lucide-react";
import Header from "../layouts/Header";
import useProfileStore from "../store/patient/profileStore";
import useAIChat from "../components/aiAssistant/useAIChat";
import useAgentBridge from "../components/aiAssistant/useAgentBridge";
import { formatTime, renderMarkdownLite } from "../components/aiAssistant/chatFormatting";

/* ===================== theme ===================== */

const primaryColor = "#0cb785";
const gradientBg = "linear-gradient(135deg, #0cb785 0%, #0a9c72 100%)";
const agentColor = "#6366f1";
const textColor = "#1e293b";
const mutedColor = "#64748b";
const borderColor = "#e2e8f0";

/* ===================== animations ===================== */

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`;

const blink = keyframes`
  0%, 100% { opacity: 0.2; }
  50% { opacity: 1; }
`;

/* ===================== layout ===================== */

const ChatContainer = styled.div`
  height: calc(100vh - 7rem);
  display: flex;
  gap: 1.5rem;
  margin-top: 5rem;
  padding: 1rem;
  background: #f5f7fb;
`;

const MainChatArea = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  margin-left: 17rem;
  min-width: 0;

  @media (max-width: 1000px) {
    margin-left: 0;
  }
`;

const PageHeader = styled.div`
  margin-bottom: 1.25rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  animation: ${fadeIn} 0.4s ease-out;
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const BotIconWrapper = styled.div`
  width: 3rem;
  height: 3rem;
  border-radius: 1rem;
  background: ${(props) => (props.$agentMode ? `linear-gradient(135deg, ${agentColor} 0%, #4f46e5 100%)` : gradientBg)};
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 10px 20px rgba(12, 183, 133, 0.25);
  color: #fff;
`;

const Title = styled.h1`
  font-size: 1.4rem;
  font-weight: 700;
  color: ${textColor};
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0;
`;

const Subtitle = styled.p`
  color: ${mutedColor};
  font-size: 0.85rem;
  margin: 0.2rem 0 0 0;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const StatusDot = styled.i`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${(props) => (props.$online ? "#22c55e" : "#fbbf24")};
  display: inline-block;
`;

const NewChatButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.55rem 0.9rem;
  border-radius: 10px;
  border: 1.5px solid ${borderColor};
  background: #fff;
  color: ${textColor};
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.18s;
  flex-shrink: 0;

  svg {
    color: ${primaryColor};
  }

  &:hover {
    border-color: ${primaryColor};
    transform: translateY(-1px);
  }
`;

const ChatWindow = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 1rem;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(0, 0, 0, 0.05);
  min-height: 0;
`;

const AgentBanner = styled.div`
  padding: 0.6rem 1.25rem;
  background: #eef2ff;
  border-bottom: 1px solid #c7d2fe;
  color: #3730a3;
  font-size: 0.82rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const MessagesContainer = styled.div`
  flex: 1;
  padding: 1.5rem;
  overflow-y: auto;
  background: #f8fafc;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
  }
`;

const MessageRow = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1.1rem;
  animation: ${fadeIn} 0.25s ease-out;

  ${(props) =>
    props.$isUser &&
    css`
      flex-direction: row-reverse;
    `}
`;

const Avatar = styled.div`
  width: 2.4rem;
  height: 2.4rem;
  border-radius: 0.7rem;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  ${(props) => {
    if (props.$sender === "user") {
      return css`
        background: ${primaryColor};
        color: #fff;
      `;
    }
    if (props.$sender === "agent") {
      return css`
        background: ${agentColor};
        color: #fff;
      `;
    }
    return css`
      background: #fff;
      color: ${primaryColor};
      border: 2px solid ${primaryColor};
    `;
  }}
`;

const Bubble = styled.div`
  max-width: 70%;
  border-radius: 1rem;
  padding: 0.85rem 1rem;
  font-size: 0.9rem;
  line-height: 1.55;
  white-space: pre-wrap;
  word-break: break-word;

  ${(props) => {
    if (props.$sender === "user") {
      return css`
        background: ${primaryColor};
        color: #fff;
        border-top-right-radius: 0.35rem;
      `;
    }
    if (props.$sender === "agent") {
      return css`
        background: #eef2ff;
        color: ${textColor};
        border: 1px solid #c7d2fe;
        border-top-left-radius: 0.35rem;
      `;
    }
    return css`
      background: #fff;
      color: ${textColor};
      border: 1px solid ${borderColor};
      border-top-left-radius: 0.35rem;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
    `;
  }}

  ${(props) =>
    props.$error &&
    css`
      background: #fef2f2;
      border-color: #fecaca;
    `}

  p {
    margin: 0 0 0.4rem 0;

    &:last-child {
      margin-bottom: 0;
    }
  }

  code {
    background: rgba(0, 0, 0, 0.06);
    border-radius: 4px;
    padding: 0.1em 0.35em;
    font-size: 0.85em;
  }

  /* Auto-linked emails / URLs — make them clearly clickable. */
  a:not(.chat-link-btn) {
    color: ${(props) => (props.$sender === "user" ? "#eafff5" : primaryColor)};
    font-weight: 600;
    text-decoration: underline;
    word-break: break-word;

    &:hover {
      opacity: 0.85;
    }
  }

  a.chat-link-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-top: 0.5rem;
    padding: 0.55rem 1.1rem;
    background: ${gradientBg};
    color: #fff;
    font-weight: 600;
    font-size: 0.85rem;
    border-radius: 10px;
    text-decoration: none;
    box-shadow: 0 3px 10px rgba(12, 183, 133, 0.28);
    transition: transform 0.15s ease, box-shadow 0.15s ease;

    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 6px 16px rgba(12, 183, 133, 0.38);
    }
  }
`;

const SenderLabel = styled.div`
  font-size: 0.68rem;
  font-weight: 700;
  color: ${agentColor};
  margin-bottom: 3px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
`;

const Timestamp = styled.div`
  font-size: 0.7rem;
  opacity: 0.65;
  margin-top: 5px;
`;

const TypingDots = styled.span`
  display: inline-flex;
  gap: 4px;
  padding: 4px 0;

  i {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: ${mutedColor};
    animation: ${blink} 1.2s infinite;

    &:nth-child(2) {
      animation-delay: 0.2s;
    }

    &:nth-child(3) {
      animation-delay: 0.4s;
    }
  }
`;

const InputArea = styled.div`
  padding: 1rem;
  border-top: 1px solid ${borderColor};
  background: #fff;
  display: flex;
  gap: 0.75rem;
`;

const TextInput = styled.textarea`
  flex: 1;
  resize: none;
  max-height: 120px;
  min-height: 48px;
  padding: 0.7rem 1rem;
  border: 2px solid ${borderColor};
  border-radius: 0.75rem;
  font-size: 0.9rem;
  color: ${textColor};
  font-family: inherit;
  line-height: 1.4;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: ${primaryColor};
  }

  &:disabled {
    background: #f8fafc;
  }
`;

const SendButton = styled.button`
  width: 48px;
  height: 48px;
  border-radius: 0.75rem;
  background: ${gradientBg};
  color: #fff;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  align-self: flex-end;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(12, 183, 133, 0.3);
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

/* ===================== sidebar ===================== */

const SideColumn = styled.div`
  width: 300px;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  flex-shrink: 0;

  @media (max-width: 1200px) {
    display: none;
  }
`;

const SidebarCard = styled.div`
  background: #fff;
  border-radius: 1rem;
  padding: 1.25rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
  border: 1px solid rgba(0, 0, 0, 0.05);
`;

const SidebarTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: ${textColor};
  margin: 0 0 1rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const QuestionButton = styled.button`
  width: 100%;
  display: flex;
  align-items: flex-start;
  gap: 0.7rem;
  padding: 0.75rem;
  border-radius: 0.75rem;
  background: #f8fafc;
  border: 1.5px solid ${borderColor};
  text-align: left;
  margin-bottom: 0.5rem;
  cursor: pointer;
  transition: all 0.18s;

  &:hover {
    background: #fff;
    border-color: ${primaryColor};
    transform: translateX(2px);
  }
`;

const QuestionIcon = styled.div`
  width: 2rem;
  height: 2rem;
  border-radius: 0.5rem;
  background: rgba(12, 183, 133, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${primaryColor};
  flex-shrink: 0;
`;

const QuestionMain = styled.p`
  margin: 0;
  font-size: 0.85rem;
  color: ${textColor};
`;

const QuestionCategory = styled.p`
  margin: 0.2rem 0 0 0;
  font-size: 0.72rem;
  color: ${mutedColor};
`;

const InfoCard = styled.div`
  background: linear-gradient(135deg, rgba(12, 183, 133, 0.08) 0%, rgba(10, 156, 114, 0.12) 100%);
  border-radius: 1rem;
  padding: 1.25rem;
  border: 1px solid rgba(12, 183, 133, 0.2);
`;

/* ===================== data ===================== */

const suggestedQuestions = [
  { icon: FaCalendarAlt, text: "Is there a doctor available tomorrow?", category: "Availability" },
  { icon: FaUserMd, text: "Book an appointment", category: "Appointments" },
  { icon: FaFileAlt, text: "What do my latest vitals look like?", category: "Records" },
  { icon: FaPills, text: "Order medication from the pharmacy", category: "Drug Mart" },
  { icon: FaFileAlt, text: "Book a lab test", category: "Lab Mart" },
];

/* ===================== component ===================== */

export default function MedicalChatbot() {
  const { profile } = useProfileStore();
  const [input, setInput] = useState("");
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const currentSkin = localStorage.getItem("skin-mode") ? "dark" : "";
  const [skin, setSkin] = useState(currentSkin);

  const patientId = useMemo(() => {
    if (profile && !Array.isArray(profile) && profile.id) {
      return profile.id;
    }
    return window.localStorage.getItem("userId") || null;
  }, [profile]);

  const {
    messages,
    sending,
    connected,
    agentSession,
    sendMessage,
    startNewConversation,
  } = useAIChat({ patientId, isOpen: true });

  const liveAgent = useAgentBridge({ patientId, patient: profile });
  const liveMode = liveAgent.status !== "idle";
  const displayMessages = liveMode ? liveAgent.messages : messages;

  const agentMode =
    liveMode ||
    Boolean(agentSession && (agentSession.status === "active" || agentSession.queued));

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, liveAgent.messages]);

  const handleSend = () => {
    const text = input;
    if (!text.trim()) return;
    setInput("");
    if (liveMode) {
      liveAgent.sendMessage(text);
    } else {
      sendMessage(text);
    }
    inputRef.current?.focus();
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  const statusLabel = liveMode
    ? liveAgent.status === "connected"
      ? `Connected to ${liveAgent.agentName || "a support agent"}`
      : liveAgent.status === "waiting"
        ? "Waiting for an agent to pick up…"
        : "Reconnecting…"
    : agentMode
      ? agentSession.status === "active"
        ? `Connected to ${agentSession.agentName || "a support agent"}`
        : `In queue${agentSession.queuePosition ? ` — position ${agentSession.queuePosition}` : ""}`
      : connected
        ? "Online — replies in real time"
        : "Connecting…";

  return (
    <React.Fragment>
      <Header onSkin={setSkin} />
      <ChatContainer>
        <MainChatArea>
          <PageHeader>
            <HeaderContent>
              <BotIconWrapper $agentMode={agentMode}>
                {agentMode ? <FaHeadset size={22} /> : <FaRobot size={22} />}
              </BotIconWrapper>
              <div>
                <Title>
                  {agentMode ? "Support Team" : "Anna"} <Star size={18} color={primaryColor} />
                </Title>
                <Subtitle>
                  <StatusDot $online={connected} />
                  {statusLabel}
                </Subtitle>
              </div>
            </HeaderContent>
            {liveMode ? (
              <NewChatButton onClick={liveAgent.endSession}>
                <FaRobot size={12} /> Back to Anna
              </NewChatButton>
            ) : (
              <>
                <NewChatButton onClick={() => liveAgent.startSession()} style={{ marginRight: "0.5rem" }}>
                  <FaHeadset size={12} /> Talk to a human
                </NewChatButton>
                <NewChatButton onClick={startNewConversation}>
                  <FaPlus size={12} /> New conversation
                </NewChatButton>
              </>
            )}
          </PageHeader>

          <ChatWindow>
            {liveMode ? (
              <AgentBanner>
                <FaHeadset size={13} />
                {liveAgent.status === "connected"
                  ? `You're chatting with ${liveAgent.agentName || "our support team"}. Messages go directly to your agent.`
                  : "Connecting you with our support team — you'll be able to chat as soon as an agent picks up."}
              </AgentBanner>
            ) : (
              agentMode && (
                <AgentBanner>
                  <FaHeadset size={13} />
                  {agentSession.status === "active"
                    ? "You're chatting with our support team. Messages go directly to your agent."
                    : "All agents are busy right now — you're in the queue and will be connected shortly."}
                </AgentBanner>
              )
            )}

            <MessagesContainer>
              {displayMessages.map((message) =>
                message.sender === "system" ? (
                  <MessageRow key={message.id} style={{ justifyContent: "center" }}>
                    <Bubble $sender="bot" style={{ maxWidth: "90%", textAlign: "center", fontStyle: "italic", opacity: 0.8 }}>
                      <span style={{ whiteSpace: "pre-wrap" }}>{message.text}</span>
                    </Bubble>
                  </MessageRow>
                ) : (
                  <MessageRow key={message.id} $isUser={message.sender === "user"}>
                    <Avatar $sender={message.sender}>
                      {message.sender === "user" ? (
                        <FaUser size={14} />
                      ) : message.sender === "agent" ? (
                        <FaHeadset size={14} />
                      ) : (
                        <FaRobot size={14} />
                      )}
                    </Avatar>
                    <Bubble $sender={message.sender} $error={message.error}>
                      {message.sender === "agent" && (
                        <SenderLabel>{message.agentName || "Support Agent"}</SenderLabel>
                      )}
                      {message.streaming && !message.text ? (
                        <TypingDots>
                          <i />
                          <i />
                          <i />
                        </TypingDots>
                      ) : message.sender === "user" ? (
                        <span style={{ whiteSpace: "pre-wrap" }}>{message.text}</span>
                      ) : (
                        <span
                          dangerouslySetInnerHTML={{ __html: renderMarkdownLite(message.text) }}
                        />
                      )}
                      <Timestamp>{formatTime(message.timestamp)}</Timestamp>
                    </Bubble>
                  </MessageRow>
                ),
              )}

              {!liveMode && sending && !messages.some((message) => message.streaming) && (
                <MessageRow>
                  <Avatar $sender="bot">
                    <FaRobot size={14} />
                  </Avatar>
                  <Bubble $sender="bot">
                    <TypingDots>
                      <i />
                      <i />
                      <i />
                    </TypingDots>
                  </Bubble>
                </MessageRow>
              )}

              <div ref={messagesEndRef} />
            </MessagesContainer>

            <InputArea>
              <TextInput
                ref={inputRef}
                rows={1}
                value={input}
                onChange={(event) => setInput(event.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={liveMode || agentMode ? "Message the support team…" : "Ask about your health, appointments, availability…"}
                disabled={!liveMode && sending}
                aria-label="Type your message"
              />
              <SendButton
                onClick={handleSend}
                disabled={!input.trim() || (!liveMode && sending)}
                aria-label="Send message"
              >
                <FaPaperPlane size={16} />
              </SendButton>
            </InputArea>
          </ChatWindow>
        </MainChatArea>

        <SideColumn>
          <SidebarCard>
            <SidebarTitle>
              <FaRobot size={18} color={primaryColor} />
              Quick Questions
            </SidebarTitle>
            {suggestedQuestions.map((question) => (
              <QuestionButton
                key={question.text}
                onClick={() => sendMessage(question.text)}
                disabled={sending}
              >
                <QuestionIcon>
                  <question.icon size={14} />
                </QuestionIcon>
                <div>
                  <QuestionMain>{question.text}</QuestionMain>
                  <QuestionCategory>{question.category}</QuestionCategory>
                </div>
              </QuestionButton>
            ))}
          </SidebarCard>

          <InfoCard>
            <Star size={26} color={primaryColor} style={{ marginBottom: "0.6rem" }} />
            <h3 style={{ margin: "0 0 0.4rem 0", fontSize: "1rem", fontWeight: 600 }}>
              AI-Powered Support
            </h3>
            <p style={{ margin: 0, fontSize: "0.85rem", color: mutedColor }}>
              Anna can check doctor availability, book appointments, explain your records, and hand you over to a live support agent when needed.
            </p>
          </InfoCard>
        </SideColumn>
      </ChatContainer>
    </React.Fragment>
  );
}
